import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Mode, EventOrder, OddsMode, BetTypeGroup, OddsFormat, MarketFilter, Currency, 
  Market, OUTRIGHT_TYPE, TeaserSports, DEFAULT_DATES, PrefsMap } from '../common/Foundation';
import CommonUtil from '../common/CommonUtil';
import _ from 'lodash';
import cookie from 'react-cookies';
import moment from 'moment-timezone'
import { } from '../common/Extensions'
import Constants from '../common/Constants';
import {batch} from "react-redux";
import {FavouritesSlice} from "./FavouritesSlice";
import MemberServices from '../services/MemberService';
import { GROUP_SPORT_PERIODS } from '../common/Foundation';


/**
 * Build State data for sport pages
 * Author: Oliver.Nguyen
 */

const defaultTeaser = {
  sportId: TeaserSports.Basketball,
  typeId: 0,
  point: 0,
  date: '',
};

const defaultTeaserData = {
  sportId: TeaserSports.Basketball,
  teaserDates: [DEFAULT_DATES[0]],
  teaserLeagues: [],
  teaserEvents: [],
  teaserTypes: [],
};

const defaultLiveCenter = {
  eventId: 0,
  selectedEvent: {},
  events: [],
  eventDetails: {},
  urls: [],
};
const defaultLiveTV = {
  eventId: 0,
  selectIdFirstTime: false,
  selectedEvent: {},
  events: [],
  eventDetails: {},
  urls: [],
};
const defaultStats = {
  eventId: 0,
  selectedEvent: {},
  events: [],
  eventDetails: {},
};

const defaultFavoriteData = {
  leagueIds: [],
  eventIds: []
}

const searchData = {
  searchSportId: '',
  searchEventsIds: '',
  searchText: '',
  searchLeagueId: '',
  searchType: '',
  isFirstLoading: true,
  isRefreshing: false,
  searchOuEventIds: ''
};

const loadPrefsOptions = () => {
  const options = {};
  options.ogCache = cookie.load('_og') || '';
  const __prefs = _.isEmpty(cookie.load('__prefs')) ? '' : CommonUtil.decodeBase64(cookie.load('__prefs'));
  const __prefsJSON = _.isEmpty(__prefs) ? {} : CommonUtil.convertStringToJSON(__prefs);
  const perfs = localStorage.getItem('perfs') || {};
  const lastestOddsFormat = localStorage.getItem('oddsFormat');
  // Update options by __prefs of user
  if (!_.isEmpty(__prefsJSON)) {
    options.timeZoneOffset = __prefsJSON[PrefsMap.timeZoneOffset] === null ? moment().utcOffset() : __prefsJSON[PrefsMap.timeZoneOffset];

    // Update DateTZ.defaultTZOffset follow timezone set in Preferences
    if (typeof (options.timeZoneOffset) !== 'undefined' && options.timeZoneOffset != null) {
      window.DateTZ.defaultTZOffset = options.timeZoneOffset;
    } else {
      window.DateTZ.defaultTZOffset = window.DateTZ.localTimeZone;
    } 
    options.oddsFormat = lastestOddsFormat ? Number(lastestOddsFormat, 10) : __prefsJSON[PrefsMap.oddsFormat];
    options.oddsMode = __prefsJSON[PrefsMap.oddsMode];
    options.market = __prefsJSON[PrefsMap.market] === 0 ? Market.Today : Market.ALL;
    options.betTypeGroup = __prefsJSON[PrefsMap.market] === 0 ? BetTypeGroup.HDP_OU : (__prefsJSON[PrefsMap.market] === 1 ? BetTypeGroup.ML_1X2 : BetTypeGroup.TEAM_TOTAL);
    options.order = CommonUtil.convertEventSorting(__prefsJSON[PrefsMap.order]);
    options.sportsSorting = __prefsJSON[PrefsMap.sportsSorting] && ("," + __prefsJSON[PrefsMap.sportsSorting] + ",");
    options.enableDefaultStake = __prefsJSON[PrefsMap.enableDefaultStake];
    options.defaultStake = __prefsJSON[PrefsMap.defaultStake];
    options.acceptBetterOdds = __prefsJSON[PrefsMap.acceptBetterOdds];
    options.updateBetslipOdds = __prefsJSON[PrefsMap.updateBetslipOdds];
    options.marketFilter = CommonUtil.castToMarketFilter(__prefsJSON[PrefsMap.marketFilter]) || 5;
    options.liveScoreNotify = __prefsJSON.length >= Constants.ESPORT_ID ? __prefsJSON[PrefsMap.liveScoreNotify] : 0;
    options.timeZoneId = __prefsJSON[PrefsMap.timeZoneId] || moment.tz.guess();
    options.liveContainerOnTopEnabled = __prefsJSON[PrefsMap.liveContainerOnTopEnabled]
  } 
  const oldPrefs = !_.isEmpty(perfs) ? CommonUtil.convertStringToJSON(atob(perfs)) : {};
  options.favouriteData = oldPrefs.favouriteData || defaultFavoriteData;

  if (!_.isEmpty(perfs)) {
    options.sound = _.isNil(oldPrefs.sound) ? true : oldPrefs.sound;
    options.favourite = oldPrefs.favourite || false;
    options.favouriteSportId = oldPrefs.favouriteSportId || Constants.SOCCER_ID;
    options.favouriteData = oldPrefs.favouriteData || defaultFavoriteData;
    if (_.isNull(options.oddsFormat)) {
      options.oddsFormat = lastestOddsFormat ? Number(lastestOddsFormat, 10) : oldPrefs.oddsFormat;
    }
    if(options.favourite && (options.favouriteEventId || options.favoriteLeagueId)) {
      options.sportId = oldPrefs.favouriteSportId;
      options.betTypeGroup = BetTypeGroup.HDP_OU;
      options.market = Market.ALL;
    }
  }

  return options
}

const options = {
  sportId: 0,
  leagueIds: [],
  market: Market.Today,
  currency: Currency.USD,
  marketFilter: MarketFilter._3Lines,
  oddsFormat: OddsFormat.EU,
  favourite: false,
  favouriteSportId: 0,
  filterOptions: {
    selectedDate: ''
  },
  hasLiveTVEvent: true,
  betTypeGroup: BetTypeGroup.HDP_OU,
  oddsFormatSetting: "EU,HK,MY,AM",
  oddsMode: OddsMode.Double,
  order: EventOrder.League,
  ogCache: '',
  timeFilter: 0,
  leagues: '',
  mode: Mode.NORMAL,
  moreOption: false,
  parlay: {},
  teaser: defaultTeaser,
  eSports: {
    eSportId: '',
    eSportCode: '',
    eSportLeagueIds: [],
  },
  outright: {
    sportId: 29,
    leagueIds: {},
    outrightType: OUTRIGHT_TYPE.OUTRIGHT,
  },
  virtualSports: {
    sportId: ''
  },
  isTopLeague: false,
  topLeagueLeagueId: null,
  enableConfirmBet: false
}

export const getSportPeriodDesc = createAsyncThunk(
  'sport/getSportPeriodDesc',
  async (_,  { rejectWithValue }) => {
    try {
      const response = await MemberServices.getSportPeriodDesc();
      return response.data;
    } catch(err) {
      return rejectWithValue([]);
    }
  }
)

export const SportsSlice = createSlice({
  name: 'SportsSlice',
  initialState: {
    isReady: false,
    timeZoneOffset: null,
    teaserData: defaultTeaserData,
    isFetching: false,
    isLiveFetching: false,
    isNonLiveFetching: false,
    isHightLightFetching: false,
    options: Object.assign(options, loadPrefsOptions()),
    liveCenter: defaultLiveCenter,
    liveTV: defaultLiveTV,
    stats: defaultStats,
    isSelectedHighlight: false,
    isEarlyPageScroll: false,
    search: searchData,
    periodsDescription: [],
    isShowBannerDelay: true,
    previousMode: Mode.NORMAL, // Keep previous mode in case Live Center/Live TV and go back
    isHasDynamicParameter: false,
    retryWebSocketNumber: 0
  },
  reducers: {
    updateRetryWebSocketNumber: (state, {payload}) => {
      state.retryWebSocketNumber = payload;
    },
    updateIsSelectedHighlight: (state, { payload = false }) => {
      if (!_.isEqual(state.isSelectedHighlight, payload)) {
        state.isSelectedHighlight = payload;
      }
    },
    updateOptions: (state, { payload }) => {
      if (typeof payload !== 'object') {
        return;
      }
      Object.assign(state.options, payload);
      localStorage.setItem('perfs', btoa(JSON.stringify(state.options)));
    },

    updateFavouritesOptions: (state, { payload }) => {
      if (typeof payload !== 'object') {
        return;
      }
      Object.assign(state.options, payload);
      localStorage.setItem('perfs', btoa(JSON.stringify(state.options)));
      localStorage.setItem('favPerfs', btoa(JSON.stringify(payload)));
    },

    updateOption: (state, { payload }) => {
      if (!payload || _.isEmpty(payload.value)) {
        return;
      }
      state.options[payload.key] = payload.value;
    },
    updateChangedOptions: (state, { payload }) => {
      if (typeof payload !== 'object') {
        return;
      }
      _.forEach(payload, (value, key) => {
        const currentValue = state.options[key];
        if (!_.isEqual(currentValue, value)) {
          state.options[key] = value;
        }
      });
      localStorage.setItem('perfs', btoa(JSON.stringify(state.options)));
    },
    updateTeaserFilter: (state, { payload = {}}) => {
      _.forEach(payload, (value, key) => {
        const currentValue = state.options.teaser[key];
        if (!_.isEqual(currentValue, value)) {
          state.options.teaser[key] = value;
        }
      });
    },
    updateTeaserData: (state, { payload = defaultTeaserData }) => {
      const {
        availableSport,
        sportId
      } = payload;
      if (!_.isEmpty(availableSport)) {
        state.options.teaser = {
          ...state.options.teaser,
          ...availableSport
        }
      }

      if (sportId !== state.teaserData.sportId || _.isEmpty(state.teaserData.teaserTypes)) {
        const newTeaserTypes = []; 
        _.forEach(payload.teaserTypes, (value, key) => {
          let teaserType = _.find(state.teaserData.teaserTypes, t => t.id === value.id);
          if (teaserType) {
            teaserType = Object.assign(teaserType, value);
          } else {
            teaserType = value;
          }
          newTeaserTypes.push(teaserType);
        });
        state.teaserData.teaserTypes = newTeaserTypes;
      }
      _.forEach(payload, (value, key) => {
        const currentValue = state.teaserData[key];
        if (key !== 'teaserTypes') {
          if (currentValue && !_.isEqual(currentValue, value)) {
            state.teaserData[key] = value;
          }
        }
      });
    },

    updateIsEarlyPageScroll: (state, { payload }) => {
      state.isEarlyPageScroll = payload;
    },

    startFetchingEvents: (state, { payload }) => {
      state.isFetching = true;
    },

    completeFetchingEvents: (state, { payload }) => {
      state.isFetching = false;
    },

    updateLiveFetchingEvents: (state, { payload }) => {
      state.isLiveFetching = payload;
    },

    updateNonLiveFetchingEvents: (state, { payload }) => {
      state.isNonLiveFetching = payload;
    },

    updateHightLightFetchingEvents: (state, { payload }) => {
      state.isHightLightFetching = payload;
    },

    updateLiveCenter: (state, { payload }) => {
      _.forEach(payload, (value, key) => {
        const currentValue = state.liveCenter[key];
        if (currentValue && !_.isEqual(currentValue, value)) {
          state.liveCenter[key] = value;
        }
      });

      const { events, selectedEvent } = state.liveCenter;
      if (!_.isEmpty(events) && _.isEmpty(selectedEvent)) {
        state.liveCenter.selectedEvent = events[0] || {};
      }
    },

    updateStats: (state, { payload }) => {
      _.forEach(payload, (value, key) => {
        const currentValue = state.stats[key];
        if (currentValue && !_.isEqual(currentValue, value)) {
          state.stats[key] = value;
        }
      });

      const { events, selectedEvent } = state.stats;
      if (!_.isEmpty(events) && _.isEmpty(selectedEvent)) {
        state.stats.selectedEvent = events[0] || {};
      }
    },

    updateLiveTV: (state, { payload }) => {
      let list_events = []
      payload.live && _.forEach(payload.live, selection =>{
        selection.isLive = true;
        list_events.push(selection);
        state.liveTV.events = list_events;
      })
      state.liveTV.selectIdFirstTime = payload.selectIdFirstTime > 0 ? payload.selectIdFirstTime : state.liveTV.selectIdFirstTime
      payload.nonLive && _.forEach(payload.nonLive, selection => {
        selection.isLive = false;
          list_events.push(selection)
          state.liveTV.events = list_events;
      })
      const { events, selectedEvent } = state.liveTV;
      if (!_.isEmpty(events) && _.isEmpty(selectedEvent)) {
        state.liveTV.selectedEvent = events[0] || {};
        state.liveTV.eventDetails = events[0] || {};
      }
    },
    updateSelectedEvent: (state, { payload }) => {
      state.liveTV.selectedEvent = payload;
      state.liveTV.eventDetails = payload;
    },
    resetLiveCenter: (state, { payload }) => {
      state.options.mode = payload;
      state.liveCenter = defaultLiveCenter;
    },
    resetLiveTV: (state, { payload }) => {
      state.options.mode = payload;
      state.liveTV = defaultLiveTV;
    },
    resetStats: (state, { payload }) => {
      state.options.mode = payload;
      state.stats = defaultStats;
    },
    updatePreviousMode: (state, { payload }) => {
      state.previousMode = payload;
    },
    updateSearchData: (state, {payload}) => {
      state.search = payload;
    },
    updateSearchRefresh: (state, {payload}) => {
      state.search.isRefreshing = payload;
    },
    updatePeriodsDescription: (state, {payload}) => {
      state.periodsDescription = payload;
    },
    updateShowBannerDelay: (state, {payload}) => {
      state.isShowBannerDelay = payload
    },
    resetSearchData: (state, {payload}) => {
      state.options.mode = payload && payload.mode ? payload.mode : Mode.NORMAL;
      state.search = {
          searchLeagueId: '',
          searchEventsIds: '',
          searchText: '',
          sportId: '',
          searchType: '',
          isFirstLoading: false,
          isRefreshing: false
      };
    },
    updateHasDynamicParameter: (state, { payload }) => {
      state.isHasDynamicParameter = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSportPeriodDesc.fulfilled, (state, action) => {
      const data = action.payload
      const periodsDesc = data.reduce((sportPeriods, periods) => {
        sportPeriods.push({
          sportId: periods[GROUP_SPORT_PERIODS.sportId],
          periodsDescription: periods[GROUP_SPORT_PERIODS.groupPeriod]
        });
        return sportPeriods;
      }, []);
      state.periodsDescription = periodsDesc;
    })
  }
});

export const {
  updateChangedOptions,
  updateLiveCenter,
  updateLiveTV,
  updateStats,
  updateSelectedEvent,
  resetLiveCenter,
  resetLiveTV,
  resetStats,
  updateIsSelectedHighlight,
  resetSearchData,
  updateSearchRefresh,
  updateFavouritesOptions,
  updatePeriodsDescription,
  updateShowBannerDelay,
  updateHasDynamicParameter,
  updateRetryWebSocketNumber
} = SportsSlice.actions

export default SportsSlice.reducer;

export const getSelectedEvent = state => state.SportsSlice.liveCenter.selectedEvent;
export const getSelectedLiveEvents = state => state.SportsSlice.liveCenter.events;
export const getLiveEventDetails = state => state.SportsSlice.liveCenter.eventDetails;
export const getSelectedHighlight = state => state.SportsSlice.isSelectedHighlight;

export const getSelectedEventTV = state => state.SportsSlice.liveTV.selectedEvent;
export const getSelectedLiveEventsTV = state => state.SportsSlice.liveTV.events;
export const getLiveEventDetailsTV = state => state.SportsSlice.liveTV.eventDetails;
export const getSelectIdFirstTime =  state => state.SportsSlice.liveTV.selectIdFirstTime;

export const updateOptionsFromUdata = (__udata) => {
  return async (dispatch, getSlice) => {
    const updatedClassicOptions = {};
    const updatedOptions = {};
    // destract odds setting from __udata
    const oddsSetting = __udata.oddsFormatSetting.split(',');
    const defaultOddsFormat = OddsFormat[oddsSetting[0]];
    // Classic View
    updatedClassicOptions['oddsFormat'] = defaultOddsFormat;
    updatedClassicOptions['oddsFormatSetting'] = __udata.oddsFormatSetting;
    // Other View
    const currentOddsFormat = getSlice().SportsSlice.options.oddsFormat
    /**
     *  Max Bet Reference BASE
     *  CASE: User selected non-AM in Parlay/Teaser and Refresh page (F5)
     * 
     * */
    if (__udata.maxBetPreferenceSettingBase) {
      updatedOptions['oddsFormat'] = OddsFormat.AM;
    } else if (_.isNil(currentOddsFormat) || !_.includes(oddsSetting, _.invert(OddsFormat)[currentOddsFormat])) {
      updatedOptions['oddsFormat'] = defaultOddsFormat;
    }
    updatedOptions['oddsFormatSetting'] = __udata.oddsFormatSetting;
    updatedOptions['sportSuppLS'] = __udata.sportSuppLS;
    updatedOptions['liveBlockedSports'] = __udata.liveBlockedSports;
    // ClassicSportsStore.updateOptions(updatedClassicOptions)
    dispatch(updateChangedOptions(updatedOptions));
  }
}

export const updateFavouritesAndAllowFetchFavourites = (favData) => {
  return (dispatch) => {
    batch(() => {
      dispatch(FavouritesSlice.actions.updateIsAllowFetchFavourites(true));
      dispatch(updateFavouritesOptions({favouriteData: favData}));
    })
  }
}



// WEBPACK FOOTER //
// ./src/app/slices/SportsSlice.js